
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Member, FriendRequest } from './types';
import MembersTable from './members/MembersTable';
import { useFriendRequests } from './hooks/useFriendRequests';

interface MembersListProps {
  state: string;
  lga: string;
  isAdmin: boolean;
}

const MembersList = ({ state, lga, isAdmin }: MembersListProps) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { friendRequests, sendFriendRequest, acceptFriendRequest } = useFriendRequests(user?.id);
  
  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('community_members')
          .select('id, full_name, phone_number, email, state, local_government, created_at, nickname, avatar_url, invite_count')
          .eq('state', state)
          .eq('local_government', lga)
          .eq('approved', true)
          .order('full_name');
        
        if (error) throw error;
        setMembers(data || []);
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMembers();
  }, [state, lga]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="w-8 h-8 border-4 border-t-omoduduwa-terracotta border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
        <span className="ml-2">Loading members...</span>
      </div>
    );
  }
  
  if (members.length === 0) {
    return <div className="text-center py-4 text-gray-500">No approved members yet</div>;
  }

  // Convert friendRequests object to array for MembersTable
  const friendRequestsArray = Object.values(friendRequests || {});
  
  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      className="rounded-md border overflow-hidden"
    >
      <MembersTable
        members={members}
        friendRequests={friendRequestsArray}
        userId={user?.id}
        isAdmin={isAdmin}
        onSendRequest={sendFriendRequest}
        onAcceptRequest={acceptFriendRequest}
      />
    </motion.div>
  );
};

export default MembersList;
